<template>
  <div class="analytics-date-range-picker" ref="container">
    <!-- Preview Button -->
    <div
      class="picker-preview"
      :class="{ active: isOpen }"
      @click="toggleDropdown"
      tabindex="0"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.esc="closeDropdown"
    >
      <span class="preview-label">{{ displayLabel }}</span>
      <svg class="chevron" :class="{ rotated: isOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <!-- Dropdown Panel -->
    <Teleport to="body">
      <div
        v-if="isOpen && dropdownPosition"
        class="picker-dropdown"
        :style="{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`
        }"
      >
        <!-- Quick Presets -->
        <div class="dropdown-section">
          <div class="section-title">Quick Select</div>
          <div class="preset-grid">
            <button
              v-for="preset in presets"
              :key="preset.id"
              class="preset-button"
              :class="{ active: selectedPreset === preset.id }"
              @click="selectPreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>
        </div>

        <!-- Custom Date Range -->
        <div class="dropdown-section" v-if="selectedPreset === 'custom'">
          <div class="section-title">Custom Range</div>
          <div class="custom-dates">
            <div class="date-field">
              <label>From</label>
              <DateInput v-model="customStartDate" placeholder="MM/DD/YYYY" />
            </div>
            <div class="date-field">
              <label>To</label>
              <DateInput v-model="customEndDate" placeholder="MM/DD/YYYY" />
            </div>
          </div>
        </div>

        <!-- Apply Button -->
        <div class="dropdown-footer">
          <button class="apply-button" @click="applySelection">
            Apply
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import DateInput from './DateInput.vue';
import { formatLocalDate } from '@/utils/dateUtils';

export default {
  name: 'AnalyticsDateRangePicker',
  components: { DateInput },
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        startDate: null,
        endDate: null
      })
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      dropdownPosition: null,
      selectedPreset: 'last-3-months',
      customStartDate: '',
      customEndDate: '',

      presets: [
        { id: 'this-month', label: 'This Month' },
        { id: 'last-month', label: 'Last Month' },
        { id: 'last-3-months', label: 'Last 3 Months' },
        { id: 'last-6-months', label: 'Last 6 Months' },
        { id: 'ytd', label: 'Year to Date' },
        { id: 'last-year', label: 'Last Year' },
        { id: 'last-2-years', label: 'Last 2 Years' },
        { id: 'last-5-years', label: 'Last 5 Years' },
        { id: 'custom', label: 'Custom' }
      ]
    };
  },
  computed: {
    displayLabel() {
      const preset = this.presets.find(p => p.id === this.selectedPreset);
      if (preset) {
        return preset.label;
      }
      return 'Select Range';
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateDropdownPosition, true);
    window.addEventListener('resize', this.updateDropdownPosition);
    document.addEventListener('mousedown', this.handleClickOutside);

    this.initializeFromValue();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateDropdownPosition, true);
    window.removeEventListener('resize', this.updateDropdownPosition);
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
  methods: {
    initializeFromValue() {
      if (!this.modelValue?.startDate) {
        this.applySelection();
      }
    },
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    openDropdown() {
      this.isOpen = true;
      this.updateDropdownPosition();
    },
    closeDropdown() {
      this.isOpen = false;
      this.dropdownPosition = null;
    },
    handleClickOutside(event) {
      if (!this.isOpen) return;

      const container = this.$refs.container;
      const dropdown = document.querySelector('.picker-dropdown');

      if (container && !container.contains(event.target) &&
          (!dropdown || !dropdown.contains(event.target))) {
        this.closeDropdown();
      }
    },
    updateDropdownPosition() {
      if (!this.isOpen || !this.$refs.container) return;

      const rect = this.$refs.container.getBoundingClientRect();
      const gap = 4;

      this.dropdownPosition = {
        top: rect.bottom + gap,
        left: rect.left
      };
    },
    selectPreset(preset) {
      this.selectedPreset = preset.id;
    },
    getPresetDates(presetId) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      let startDate, endDate;

      switch (presetId) {
        case 'this-month':
          startDate = new Date(currentYear, currentMonth, 1);
          endDate = today;
          break;

        case 'last-month':
          startDate = new Date(currentYear, currentMonth - 1, 1);
          endDate = new Date(currentYear, currentMonth, 0);
          break;

        case 'last-3-months':
          startDate = new Date(currentYear, currentMonth - 2, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
          break;

        case 'last-6-months':
          startDate = new Date(currentYear, currentMonth - 5, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
          break;

        case 'ytd':
          startDate = new Date(currentYear, 0, 1);
          endDate = today;
          break;

        case 'last-year':
          startDate = new Date(currentYear - 1, 0, 1);
          endDate = new Date(currentYear - 1, 11, 31);
          break;

        case 'last-2-years':
          startDate = new Date(currentYear - 1, 0, 1);
          endDate = new Date(currentYear, 11, 31);
          break;

        case 'last-5-years':
          startDate = new Date(currentYear - 4, 0, 1);
          endDate = new Date(currentYear, 11, 31);
          break;

        case 'custom':
          if (this.customStartDate && this.customEndDate) {
            return {
              startDate: this.customStartDate,
              endDate: this.customEndDate
            };
          }
          startDate = new Date(currentYear, currentMonth - 2, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
          break;

        default:
          startDate = new Date(currentYear, currentMonth - 2, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
      }

      return {
        startDate: formatLocalDate(startDate),
        endDate: formatLocalDate(endDate)
      };
    },
    applySelection() {
      const dates = this.getPresetDates(this.selectedPreset);

      this.$emit('update:modelValue', {
        startDate: dates.startDate,
        endDate: dates.endDate
      });

      this.closeDropdown();
    }
  }
};
</script>

<style scoped>
.analytics-date-range-picker {
  position: relative;
  display: inline-block;
}

.picker-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 180px;
  height: 31px;
  box-sizing: border-box;
}

.picker-preview:hover {
  border-color: #9ca3af;
}

.picker-preview:focus {
  outline: none;
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
}

.picker-preview.active {
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
}

.preview-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.chevron {
  margin-left: auto;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.picker-dropdown {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 10000;
  min-width: 320px;
  overflow: hidden;
}

.dropdown-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 8px;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.preset-button {
  padding: 8px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}

.preset-button:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.preset-button.active {
  background: #e0e7ff;
  border-color: #4338ca;
  color: #4338ca;
}

.custom-dates {
  display: flex;
  gap: 12px;
}

.date-field {
  flex: 1;
}

.date-field label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
}

.dropdown-footer {
  padding: 12px 16px;
  background: #f9fafb;
  border-top: 1px solid #f0f0f0;
}

.apply-button {
  width: 100%;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: #4338ca;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background 0.15s ease;
}

.apply-button:hover {
  background: #3730a3;
}
</style>
